import { useEffect, useState } from 'react';
import { getWhatsAppAccount } from '../../services/whatsappApi';
import type { WhatsAppAccount } from '../../types/whatsapp';
import { dateTimeLabel } from '../../whatsapp/clock';
import { fetchClinicTimeZone } from '../../whatsapp/clinicTimeZone';

const LOAD_ERROR = 'No se pudo cargar la cuenta de WhatsApp';

type Status = NonNullable<WhatsAppAccount['status']>;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; account: WhatsAppAccount; timeZone: string };

interface Badge {
  label: string;
  className: string;
}

const NO_DATA: Badge = { label: 'Sin datos', className: 'bg-stone-100 text-stone-700' };

const STATUS_BADGE: Record<Status, Badge & { dot: string; icon: string }> = {
  connected: {
    label: 'Conectado',
    className: 'bg-green-100 text-green-800',
    dot: 'bg-green-600',
    icon: 'bg-green-100 text-green-800',
  },
  error: {
    label: 'Error de conexión',
    className: 'bg-amber-100 text-amber-900',
    dot: 'bg-amber-600',
    icon: 'bg-amber-100 text-amber-900',
  },
  disconnected: {
    label: 'Desconectado',
    className: 'bg-stone-100 text-stone-700',
    dot: 'bg-stone-500',
    icon: 'bg-stone-100 text-stone-700',
  },
};

const NAME_STATUS_BADGE: Record<NonNullable<WhatsAppAccount['display_name_status']>, Badge> = {
  approved: { label: 'Aprobado por Meta', className: 'bg-green-100 text-green-800' },
  pending: { label: 'Pendiente de aprobación', className: 'bg-amber-100 text-amber-900' },
  rejected: { label: 'Rechazado', className: 'bg-red-100 text-red-800' },
};

const QUALITY_BADGE: Record<string, Badge> = {
  GREEN: { label: 'Alta', className: 'bg-green-100 text-green-800' },
  YELLOW: { label: 'Media', className: 'bg-amber-100 text-amber-900' },
  RED: { label: 'Baja', className: 'bg-red-100 text-red-800' },
};

const pillClass = (badge: Badge) =>
  `inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${badge.className}`;

const WhatsAppIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-12.2 7.5L3 20l1-4.6A8.5 8.5 0 1 1 21 11.5z" />
  </svg>
);

export const WhatsAppAccountTab = () => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    Promise.all([
      getWhatsAppAccount(),
      fetchClinicTimeZone(),
    ])
      .then(([account, timeZone]) => {
        if (active) setState({ kind: 'ready', account, timeZone });
      })
      .catch((err: unknown) => {
        if (active) setState({ kind: 'error', message: err instanceof Error ? err.message : LOAD_ERROR });
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  const retry = () => {
    setState({ kind: 'loading' });
    setAttempt((n) => n + 1);
  };

  if (state.kind === 'loading') {
    return <p role="status" className="py-12 text-center text-sm text-stone-600">Cargando…</p>;
  }

  if (state.kind === 'error') {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <p className="m-0">{state.message}</p>
        <button
          type="button"
          onClick={retry}
          className="inline-flex h-10 items-center rounded-full border border-stone-200 bg-white px-4 font-semibold text-stone-800 hover:bg-stone-50"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const { account, timeZone } = state;
  const status: Status = account.status ?? (account.connected ? 'connected' : 'disconnected');
  const statusBadge = STATUS_BADGE[status];
  const nameBadge = account.display_name_status ? NAME_STATUS_BADGE[account.display_name_status] : NO_DATA;
  const qualityBadge = QUALITY_BADGE[account.quality_rating ?? ''] ?? NO_DATA;

  return (
    <section className="rounded-[20px] border border-stone-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-bold text-stone-900">Número conectado</h2>
          <p className="mt-1 text-sm text-stone-600">Los pacientes escriben a este número y el asistente responde desde aquí.</p>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusBadge.className}`}>
          <span aria-hidden="true" className={`h-2 w-2 rounded-full ${statusBadge.dot}`} />
          {statusBadge.label}
        </span>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${statusBadge.icon}`}>
          <WhatsAppIcon />
        </div>
        <div className="min-w-0">
          <p className="m-0 break-all font-geist-mono text-2xl text-stone-900">{account.display_phone ?? '—'}</p>
          {account.display_name && <p className="m-0 text-sm text-stone-600">{account.display_name}</p>}
        </div>
      </div>

      {status === 'error' && account.status_detail && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="m-0">{account.status_detail}</p>
          <p className="mt-2 text-xs">La autorización de Meta debe renovarse; por ahora esto lo hace el equipo de soporte.</p>
        </div>
      )}

      <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-stone-200 pt-6 text-sm">
        <dt className="text-stone-600">Nombre visible en WhatsApp</dt>
        <dd className="m-0 justify-self-end text-right">
          <span className={pillClass(nameBadge)}>{nameBadge.label}</span>
        </dd>

        <dt className="text-stone-600">Calidad del número</dt>
        <dd className="m-0 justify-self-end text-right">
          <span className={pillClass(qualityBadge)}>{qualityBadge.label}</span>
        </dd>

        <dt className="text-stone-600">Última actividad</dt>
        <dd className={`m-0 justify-self-end text-right text-stone-900 ${account.last_activity_at ? 'font-geist-mono text-[13px]' : ''}`}>
          {account.last_activity_at ? dateTimeLabel(account.last_activity_at, timeZone) : 'Sin actividad'}
        </dd>
      </dl>

      {account.technical && (
        <details className="mt-6 rounded-xl border border-stone-200">
          <summary className="flex min-h-11 cursor-pointer items-center px-4 font-semibold text-stone-600">Detalles técnicos</summary>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-stone-200 p-4 text-sm">
            <dt className="text-stone-600">ID de cuenta de WhatsApp Business</dt>
            <dd className="m-0 justify-self-end break-all text-right font-geist-mono text-[13px] text-stone-800">{account.technical.waba_id}</dd>
            <dt className="text-stone-600">ID del número</dt>
            <dd className="m-0 justify-self-end break-all text-right font-geist-mono text-[13px] text-stone-800">{account.technical.phone_number_id}</dd>
          </dl>
        </details>
      )}
    </section>
  );
};
